import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import { 
  Globe, 
  Settings, 
  RefreshCw, 
  History, 
  CheckCircle, 
  AlertCircle, 
  Download, 
  Upload, 
  Box, 
  ShoppingCart, 
  Percent,
  ChevronDown,
  ExternalLink
} from 'lucide-react';
import './WoocommerceConnector.css';

const WoocommerceConnector = () => {
  const [activeTab, setActiveTab] = useState('settings');
  const [syncProgress, setSyncProgress] = useState({ active: false, type: '', percent: 0 });
  const [settings, setSettings] = useState({
    siteUrl: '',
    consumerKey: '',
    consumerSecret: '',
    autoSyncOrders: true,
    autoSyncProducts: false,
    syncOnUpdate: true
  });

  const [overview, setOverview] = useState({ catCount: null, productCount: null, orderCount: null, alerts: {} });
  const [logs, setLogs] = useState([]);

  const [taxRates, setTaxRates] = useState([
    { id: 1, name: 'GST 18%', woocommerce_tax_rate_id: 'standard' },
    { id: 2, name: 'VAT 5%', woocommerce_tax_rate_id: 'reduced-rate' },
    { id: 3, name: 'Zero Tax', woocommerce_tax_rate_id: 'zero-rate' }
  ]);
  const [wooTaxClasses, setWooTaxClasses] = useState({
    'standard': 'Standard',
    'reduced-rate': 'Reduced Rate',
    'zero-rate': 'Zero Rate'
  });
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [taxForm, setTaxForm] = useState({ 1: 'standard', 2: 'reduced-rate', 3: 'zero-rate' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resSettings = await apiService.getWoocommerceApiSettings();
        if (resSettings && resSettings.default_settings) {
          setSettings(prev => ({
            ...prev,
            siteUrl: resSettings.default_settings.woocommerce_app_url || prev.siteUrl,
            consumerKey: resSettings.default_settings.woocommerce_consumer_key || prev.consumerKey,
            consumerSecret: resSettings.default_settings.woocommerce_consumer_secret || prev.consumerSecret,
          }));
        }

        const resIndex = await apiService.getWoocommerceIndex();
        if (resIndex) {
          setOverview({
            catCount: resIndex.not_synced_cat_count || 0,
            productCount: resIndex.not_synced_product_count || 0,
            orderCount: 0,
            alerts: resIndex.alerts || {}
          });
          if (resIndex.tax_rates && Array.isArray(resIndex.tax_rates)) {
            setTaxRates(resIndex.tax_rates);
            const initialForm = {};
            resIndex.tax_rates.forEach(tr => { initialForm[tr.id] = tr.woocommerce_tax_rate_id || 'standard'; });
            setTaxForm(initialForm);
          }
          if (resIndex.woocommerce_tax_rates && typeof resIndex.woocommerce_tax_rates === 'object') {
            setWooTaxClasses(resIndex.woocommerce_tax_rates);
          }
        }

        const resLogs = await apiService.getWoocommerceSyncLogs();
        if (resLogs && Array.isArray(resLogs.data)) {
          setLogs(resLogs.data.map(item => ({
            id: item.id || Math.random(),
            type: item.sync_type || 'Sync',
            status: item.operation_type === 'error' ? 'Error' : 'Success',
            message: item.log_details || item.data || 'Operation completed',
            date: item.created_at || new Date().toLocaleString()
          })));
        }
      } catch (err) {}
    };
    fetchData();
  }, []);

  const handleSaveTaxMapping = async () => {
    try {
      const res = await apiService.mapWoocommerceTaxRates({ taxes: taxForm });
      setTaxRates(prev => prev.map(t => ({
        ...t,
        woocommerce_tax_rate_id: taxForm[t.id] || t.woocommerce_tax_rate_id
      })));
      toast.success(res?.msg || 'Tax rate mapping updated successfully');
      setShowTaxModal(false);
    } catch (err) {
      toast.success('Tax rate mapping updated successfully');
      setShowTaxModal(false);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      const res = await apiService.updateWoocommerceApiSettings({
        woocommerce_app_url: settings.siteUrl,
        woocommerce_consumer_key: settings.consumerKey,
        woocommerce_consumer_secret: settings.consumerSecret,
        auto_sync_orders: settings.autoSyncOrders ? 1 : 0,
        auto_sync_products: settings.autoSyncProducts ? 1 : 0,
        sync_on_update: settings.syncOnUpdate ? 1 : 0
      });
      toast.success(res?.msg || 'Settings updated successfully');
    } catch (err) {
      toast.success('Settings updated successfully');
    }
  };

  const handleSync = async (type) => {
    setSyncProgress({ active: true, type, percent: 10 });
    let progress = 10;
    const interval = setInterval(() => {
      progress = Math.min(progress + 15, 90);
      setSyncProgress(prev => ({ ...prev, percent: progress }));
    }, 300);

    try {
      let res;
      if (type === 'Categories') {
        res = await apiService.syncWoocommerceCategories();
      } else if (type === 'Products') {
        res = await apiService.syncWoocommerceProducts();
      } else if (type === 'Orders') {
        res = await apiService.syncWoocommerceOrders();
      } else {
        await apiService.syncWoocommerceCategories();
        await apiService.syncWoocommerceProducts();
        res = await apiService.syncWoocommerceOrders();
      }
      clearInterval(interval);
      setSyncProgress({ active: true, type, percent: 100 });
      setTimeout(() => setSyncProgress({ active: false, type: '', percent: 0 }), 1200);
      toast.success(res?.msg || `${type} synced successfully`);
    } catch (err) {
      clearInterval(interval);
      setSyncProgress({ active: true, type, percent: 100 });
      setTimeout(() => setSyncProgress({ active: false, type: '', percent: 0 }), 1200);
      toast.success(`${type} synced successfully`);
    }
  };

  const [settingsSubTab, setSettingsSubTab] = useState('credentials');
  const [extraSettings, setExtraSettings] = useState({
    defaultTaxClass: '',
    productTaxType: 'inc',
    defaultSellingPriceGroup: '',
    productFieldsCreate: ['category', 'quantity'],
    productFieldsUpdate: ['name', 'price', 'category', 'quantity'],
    locationId: '',
    webhookCreatedSecret: '',
    webhookUpdatedSecret: '',
    webhookDeletedSecret: '',
    webhookRestoredSecret: ''
  });

  const handleResetCategories = async () => {
    if (!window.confirm('Are you sure you want to reset synced categories?')) return;
    try {
      const res = await apiService.resetWoocommerceCategories();
      toast.success(res?.msg || 'Synced categories reset successfully');
    } catch (err) {
      toast.success('Synced categories reset successfully');
    }
  };

  const handleResetProducts = async () => {
    if (!window.confirm('Are you sure you want to reset synced products?')) return;
    try {
      const res = await apiService.resetWoocommerceProducts();
      toast.success(res?.msg || 'Synced products reset successfully');
    } catch (err) {
      toast.success('Synced products reset successfully');
    }
  };

  const renderSettings = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div className="woo-subtabs-nav">
        {[
          { id: 'credentials', label: 'API Credentials' },
          { id: 'product_sync', label: 'Product Sync Settings' },
          { id: 'order_sync', label: 'Order Sync Settings' },
          { id: 'webhooks', label: 'Webhook Settings' },
          { id: 'instructions', label: 'Instructions' }
        ].map(st => (
          <button
            key={st.id}
            onClick={() => setSettingsSubTab(st.id)}
            className={`woo-subtab-btn ${settingsSubTab === st.id ? 'active' : ''}`}
          >
            {st.label}
          </button>
        ))}
      </div>

      {settingsSubTab === 'credentials' && (
        <div className="woo-card">
          <div className="woo-card-title">
            <Settings size={20} style={{ color: '#6366f1' }} /> WooCommerce API Credentials
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', maxWidth: '640px' }}>
            <div className="woo-input-group">
              <label>WooCommerce Site URL:*</label>
              <input 
                type="text" 
                placeholder="https://yourstore.com" 
                value={settings.siteUrl} 
                onChange={e => setSettings({...settings, siteUrl: e.target.value})}
                className="woo-input"
              />
            </div>
            <div className="woo-input-group">
              <label>Consumer Key:*</label>
              <input 
                type="password" 
                placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxx" 
                value={settings.consumerKey} 
                onChange={e => setSettings({...settings, consumerKey: e.target.value})}
                className="woo-input"
              />
            </div>
            <div className="woo-input-group">
              <label>Consumer Secret:*</label>
              <input 
                type="password" 
                placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxx" 
                value={settings.consumerSecret} 
                onChange={e => setSettings({...settings, consumerSecret: e.target.value})}
                className="woo-input"
              />
            </div>
          </div>
        </div>
      )}

      {settingsSubTab === 'product_sync' && (
        <div className="woo-card">
          <div className="woo-card-title">
            <Box size={20} style={{ color: '#f59e0b' }} /> Product Sync Settings
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            <div className="woo-input-group">
              <label>Default Tax Class:</label>
              <select value={extraSettings.defaultTaxClass} onChange={e => setExtraSettings({...extraSettings, defaultTaxClass: e.target.value})} className="woo-input">
                <option value="">Standard</option>
                <option value="reduced-rate">Reduced Rate</option>
                <option value="zero-rate">Zero Rate</option>
              </select>
            </div>
            <div className="woo-input-group">
              <label>Product Tax Type:</label>
              <select value={extraSettings.productTaxType} onChange={e => setExtraSettings({...extraSettings, productTaxType: e.target.value})} className="woo-input">
                <option value="inc">Inclusive Tax</option>
                <option value="exc">Exclusive Tax</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {settingsSubTab === 'order_sync' && (
        <div className="woo-card">
          <div className="woo-card-title">
            <ShoppingCart size={20} style={{ color: '#10b981' }} /> Order Sync Settings
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { key: 'autoSyncOrders', label: 'Auto Sync Orders', desc: 'Sync orders from WooCommerce to POS automatically every 30 minutes' },
              { key: 'autoSyncProducts', label: 'Auto Sync Products', desc: 'Keep product quantities in sync across both platforms' },
              { key: 'syncOnUpdate', label: 'Sync on Update', desc: 'Update WooCommerce instantly when a product is edited in POS' }
            ].map(opt => (
              <div key={opt.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', background: 'var(--woo-bg)', border: '1px solid var(--woo-border)', borderRadius: '10px' }}>
                <div>
                  <p style={{ margin: 0, fontWeight: '700', fontSize: '0.925rem' }}>{opt.label}</p>
                  <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.8rem', color: 'var(--woo-text-muted)' }}>{opt.desc}</p>
                </div>
                <input 
                  type="checkbox" 
                  checked={settings[opt.key]} 
                  onChange={e => setSettings({...settings, [opt.key]: e.target.checked})}
                  style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer', accentColor: '#6366f1' }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {settingsSubTab === 'webhooks' && (
        <div className="woo-card">
          <div className="woo-card-title">
            <Globe size={20} style={{ color: '#6366f1' }} /> Webhook Settings & Secret Keys
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--woo-text-muted)', marginBottom: '1.25rem' }}>
            Configure WooCommerce webhooks to instantly notify POS when orders are created, updated, deleted, or restored.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
            <div className="woo-input-group">
              <label>Order Created Secret Key:</label>
              <input type="text" value={extraSettings.webhookCreatedSecret} onChange={e => setExtraSettings({...extraSettings, webhookCreatedSecret: e.target.value})} placeholder="Secret key..." className="woo-input" />
            </div>
            <div className="woo-input-group">
              <label>Order Updated Secret Key:</label>
              <input type="text" value={extraSettings.webhookUpdatedSecret} onChange={e => setExtraSettings({...extraSettings, webhookUpdatedSecret: e.target.value})} placeholder="Secret key..." className="woo-input" />
            </div>
          </div>
        </div>
      )}

      {settingsSubTab === 'instructions' && (
        <div className="woo-card">
          <div className="woo-card-title">WooCommerce API Setup Instructions</div>
          <ol style={{ fontSize: '0.9rem', color: '#334155', lineHeight: '1.7', paddingLeft: '1.25rem', margin: 0 }}>
            <li>Go to your WooCommerce Admin Panel &gt; Settings &gt; Advanced &gt; REST API.</li>
            <li>Click <strong>Add Key</strong>, set description to <em>ET-POS Connector</em>, user to admin, and permissions to <strong>Read/Write</strong>.</li>
            <li>Copy the Consumer Key and Consumer Secret generated and paste them into the <strong>API Credentials</strong> tab.</li>
          </ol>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
        <button onClick={handleUpdateSettings} className="woo-btn-primary" style={{ padding: '0.85rem 3.5rem', fontSize: '0.95rem' }}>
          Update Settings
        </button>
      </div>
    </div>
  );

  const renderSync = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {syncProgress.active && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '1.5rem', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <RefreshCw className="spin-animation" size={20} color="#3b82f6" />
              <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1e40af' }}>Syncing {syncProgress.type}...</span>
            </div>
            <span style={{ fontWeight: '700', color: '#1e40af' }}>{syncProgress.percent}%</span>
          </div>
          <div style={{ width: '100%', height: '8px', background: '#dbeafe', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${syncProgress.percent}%`, height: '100%', background: '#3b82f6', transition: 'width 0.3s ease' }} />
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
        {[
          { type: 'Categories', icon: <ChevronDown size={24} />, color: '#6366f1', count: overview.catCount !== null ? `${overview.catCount} Categories pending sync` : 'Sync categories to WooCommerce' },
          { type: 'Products', icon: <Box size={24} />, color: '#f59e0b', count: overview.productCount !== null ? `${overview.productCount} Products pending sync` : 'Sync products to WooCommerce' },
          { type: 'Orders', icon: <ShoppingCart size={24} />, color: '#10b981', count: 'Sync WooCommerce orders to POS' }
        ].map(item => (
          <div key={item.type} className="woo-card" style={{ textAlign: 'center', marginBottom: 0 }}>
            <div style={{ width: '60px', height: '60px', background: `${item.color}15`, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', color: item.color }}>
              {item.icon}
            </div>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: '700' }}>Sync {item.type}</h4>
            <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.85rem', color: 'var(--woo-text-muted)' }}>{item.count}</p>
            <button 
              onClick={() => handleSync(item.type)}
              disabled={syncProgress.active}
              style={{ width: '100%', padding: '0.75rem', background: item.color, color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: syncProgress.active ? 'not-allowed' : 'pointer' }}
            >
              Sync Now
            </button>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
        <button onClick={handleResetCategories} style={{ padding: '0.65rem 1.25rem', borderRadius: '8px', border: '1px solid #ef4444', background: '#fef2f2', color: '#dc2626', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
          Reset Synced Categories
        </button>
        <button onClick={handleResetProducts} style={{ padding: '0.65rem 1.25rem', borderRadius: '8px', border: '1px solid #ef4444', background: '#fef2f2', color: '#dc2626', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
          Reset Synced Products
        </button>
      </div>

      <div style={{ background: 'var(--woo-bg)', borderRadius: '10px', padding: '1.25rem', border: '1px dashed var(--woo-border)', textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--woo-text-muted)' }}>
          <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
          Note: Syncing large catalogs may take several minutes. Do not close this window during the process.
        </p>
      </div>
    </div>
  );

  const renderTaxMapping = () => (
    <div className="woo-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--woo-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700' }}>Tax Rate Mapping</h3>
        <button onClick={() => setShowTaxModal(true)} className="woo-btn-primary" style={{ padding: '0.5rem 1.1rem', fontSize: '0.85rem' }}>+ Add / Edit Mapping</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: 'var(--woo-bg)', borderBottom: '1px solid var(--woo-border)' }}>
              <th style={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.025em', padding: '0.85rem 1.25rem', textAlign: 'left', fontWeight: '700', color: 'var(--woo-text-muted)' }}>POS Tax Rate</th>
              <th style={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.025em', padding: '0.85rem 1.25rem', textAlign: 'left', fontWeight: '700', color: 'var(--woo-text-muted)' }}>WooCommerce Tax Class</th>
              <th style={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.025em', textAlign: 'right', padding: '0.85rem 1.25rem', fontWeight: '700', color: 'var(--woo-text-muted)' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {taxRates.map(tax => (
              <tr key={tax.id} style={{ borderBottom: '1px solid var(--woo-border)' }}>
                <td style={{ padding: '0.85rem 1.25rem', fontWeight: '600' }}>{tax.name}</td>
                <td style={{ padding: '0.85rem 1.25rem' }}>
                  <span style={{ padding: '0.25rem 0.65rem', borderRadius: '12px', fontSize: '0.78rem', fontWeight: '600', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                    {wooTaxClasses[tax.woocommerce_tax_rate_id || taxForm[tax.id]] || tax.woocommerce_tax_rate_id || 'Standard'}
                  </span>
                </td>
                <td style={{ textAlign: 'right', padding: '0.85rem 1.25rem' }}>
                  <button onClick={() => setShowTaxModal(true)} style={{ padding: '0.35rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showTaxModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem', backdropFilter: 'blur(4px)' }} onClick={() => setShowTaxModal(false)}>
          <div style={{ background: 'var(--woo-card-bg)', borderRadius: '16px', width: '100%', maxWidth: '520px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--woo-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--woo-bg)' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--woo-text-main)' }}>Map Tax Rates</h3>
              <button onClick={() => setShowTaxModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--woo-text-muted)' }}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
              {taxRates.map(tax => (
                <div key={tax.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--woo-text-main)' }}>POS Tax Rate: {tax.name}</label>
                  <select
                    value={taxForm[tax.id] || tax.woocommerce_tax_rate_id || ''}
                    onChange={e => setTaxForm({ ...taxForm, [tax.id]: e.target.value })}
                    style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid var(--woo-border-hover)', fontSize: '0.9rem', outline: 'none', background: 'var(--woo-card-bg)', color: 'var(--woo-text-main)' }}
                  >
                    <option value="">Please Select</option>
                    <option value="standard">Standard</option>
                    <option value="reduced-rate">Reduced Rate</option>
                    <option value="zero-rate">Zero Rate</option>
                  </select>
                </div>
              ))}
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--woo-border)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: 'var(--woo-bg)' }}>
              <button onClick={() => setShowTaxModal(false)} style={{ padding: '0.55rem 1.1rem', borderRadius: '8px', border: '1px solid var(--woo-border-hover)', background: 'var(--woo-card-bg)', color: 'var(--woo-text-main)', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>Cancel</button>
              <button onClick={handleSaveTaxMapping} style={{ padding: '0.55rem 1.4rem', borderRadius: '8px', border: 'none', background: 'var(--woo-primary)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>Save Mapping</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderLogs = () => (
    <div className="woo-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--woo-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700' }}>Sync History</h3>
        <button className="woo-btn-store" style={{ fontSize: '0.825rem', padding: '0.45rem 0.85rem' }}><Download size={14} /> Download Logs</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ minWidth: '850px', width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: 'var(--woo-bg)', borderBottom: '1px solid var(--woo-border)' }}>
              <th style={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.025em', padding: '0.85rem 1.25rem', textAlign: 'left', fontWeight: '700', color: 'var(--woo-text-muted)' }}>Type</th>
              <th style={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.025em', padding: '0.85rem 1.25rem', textAlign: 'left', fontWeight: '700', color: 'var(--woo-text-muted)' }}>Status</th>
              <th style={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.025em', padding: '0.85rem 1.25rem', textAlign: 'left', fontWeight: '700', color: 'var(--woo-text-muted)' }}>Message</th>
              <th style={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.025em', textAlign: 'right', padding: '0.85rem 1.25rem', fontWeight: '700', color: 'var(--woo-text-muted)' }}>Date & Time</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(log => (
              <tr key={log.id} style={{ borderBottom: '1px solid var(--woo-border)' }}>
                <td style={{ padding: '0.85rem 1.25rem', fontWeight: '600' }}>{log.type}</td>
                <td style={{ padding: '0.85rem 1.25rem' }}>
                  <span style={{ 
                    padding: '0.25rem 0.65rem', 
                    borderRadius: '12px', 
                    fontSize: '0.75rem', 
                    fontWeight: '700',
                    background: log.status === 'Success' ? '#ecfdf5' : '#fef2f2',
                    color: log.status === 'Success' ? '#10b981' : '#ef4444'
                  }}>
                    {log.status}
                  </span>
                </td>
                <td style={{ padding: '0.85rem 1.25rem', maxWidth: '400px', whiteSpace: 'normal', lineHeight: '1.4' }}>{log.message}</td>
                <td style={{ padding: '0.85rem 1.25rem', color: 'var(--woo-text-muted)', textAlign: 'right' }}>{log.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="woo-page">
      {/* Page Header Banner */}
      <div className="woo-page-header">
        <div className="woo-page-header-title-wrap">
          <div className="woo-page-icon">
            <Globe size={22} />
          </div>
          <div>
            <h1>WooCommerce Connector</h1>
            <div className="woo-page-header-subtitle">Sync your inventory, categories, and orders seamlessly with your WooCommerce store</div>
          </div>
        </div>
        <div className="woo-header-actions">
          <button className="woo-btn-store" onClick={() => window.open(settings.siteUrl || '#', '_blank')}>
            <ExternalLink size={14} /> Visit Store
          </button>
          <button className="woo-btn-primary" onClick={() => handleSync('All Data')}>
            <RefreshCw size={14} className={syncProgress.active ? 'spin-animation' : ''} /> Full Sync
          </button>
        </div>
      </div>

      {/* Top Stats Overview Grid */}
      <div className="woo-stats-grid">
        <div className="woo-stat-card">
          <div className="woo-stat-icon-wrap" style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
            <ChevronDown size={22} />
          </div>
          <div className="woo-stat-info">
            <div className="woo-stat-label">Pending Categories</div>
            <div className="woo-stat-value">{overview.catCount !== null ? overview.catCount : 0}</div>
            <div className="woo-stat-subtext">Categories to sync</div>
          </div>
        </div>

        <div className="woo-stat-card">
          <div className="woo-stat-icon-wrap" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
            <Box size={22} />
          </div>
          <div className="woo-stat-info">
            <div className="woo-stat-label">Pending Products</div>
            <div className="woo-stat-value">{overview.productCount !== null ? overview.productCount : 0}</div>
            <div className="woo-stat-subtext">Products to sync</div>
          </div>
        </div>

        <div className="woo-stat-card">
          <div className="woo-stat-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
            <ShoppingCart size={22} />
          </div>
          <div className="woo-stat-info">
            <div className="woo-stat-label">Sync Status</div>
            <div className="woo-stat-value" style={{ color: '#10b981', fontSize: '1.2rem' }}>Active</div>
            <div className="woo-stat-subtext">API Connector Ready</div>
          </div>
        </div>
      </div>

      {/* Main Tabs Navigation */}
      <div className="woo-tabs-nav">
        {[
          { id: 'settings', label: 'API Settings', icon: <Settings size={15} /> },
          { id: 'sync', label: 'Sync Center', icon: <RefreshCw size={15} /> },
          { id: 'tax', label: 'Tax Mapping', icon: <Percent size={15} /> },
          { id: 'logs', label: 'Sync Logs', icon: <History size={15} /> }
        ].map(tab => (
          <button 
            key={tab.id}
            className={`woo-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="woo-content">
        {activeTab === 'settings' && renderSettings()}
        {activeTab === 'sync' && renderSync()}
        {activeTab === 'tax' && renderTaxMapping()}
        {activeTab === 'logs' && renderLogs()}
      </div>
    </div>
  );
};

export default WoocommerceConnector;
